import {
  Card,
  SkeletonBlock,
  Typography,
} from '@one-thd/sui-atomic-components';
import React, { useEffect, useState } from 'react';
import { useThdCustomer } from '@thd-olt-functional/customer-information';
import { BuyItAgainAPI } from '../../../api/oms/order-history';
import {
  useCommonContext,
  useExperienceContext,
  usePurchaseHistoryContext,
} from '../../../hooks/useContexts';
import { IBuyItAgainRequest } from '../../../models/purchase-history';
import { IBuyAgainItem } from '../../../models/purchase-history/buy-it-again';
import { pushEventInfo } from '../../../utils/analytics';
import { getWindow } from '../../../utils/general';
import { getSvocId } from '../../../utils/user';
import { DateRanges } from '../../../utils/DateRanges';
import { FeatureSwitches } from '../../../constants/feature-switches';

export const BuyItAgain = () => {
  const [buyAgainItems, setBuyAgainItems] = useState<IBuyAgainItem[]>([]);
  const [showError, setShowError] = useState(false);
  const { userID } = useThdCustomer();
  const { isDesktop, timezone } = useCommonContext();
  const { isConsumerApp } = useExperienceContext();
  const { orderHistoryData } = usePurchaseHistoryContext();

  const orderCount = orderHistoryData?.orderCount || 0;

  const getBuyItAgainData = async () => {
    try {
      const request: IBuyItAgainRequest = {
        customerAccountId: getSvocId() || '',
        startDate: DateRanges.last2Years.startDate,
        endDate: DateRanges.last2Years.endDate,
        offset: '8',
        timezone,
      };
      const response = await BuyItAgainAPI(request, userID);
      if (response.data) {
        setBuyAgainItems(response?.data?.buyAgainItems);
      }
    } catch (error) {
      setShowError(true);
    }
  };

  useEffect(() => {
    if (orderCount > 1 && !isConsumerApp) {
      getBuyItAgainData();
    }
  }, [orderCount]);

  if (isConsumerApp) return null;

  if (buyAgainItems.length === 0 && !showError) {
    return (
      <Card
        href="#"
        data-testid="buyItAgain"
        className={`${
          isDesktop && FeatureSwitches.enableNewLoyaltySpendUI
            ? 'sui-w-1/2'
            : ''
        }`}
      >
        <div className="sui-flex sui-flex-row sui-justify-between sui-w-full">
          <Typography weight="bold" variant="h5">
            Buy It Again
          </Typography>
          <Typography> View All </Typography>
        </div>
        <div className="sui-flex sui-gap-3">
          {[
            ...Array(
              isDesktop && FeatureSwitches.enableNewLoyaltySpendUI ? 4 : 8,
            ),
          ].map((_e, i) => (
            <div className="sui-mr-9" key={i}>
              <SkeletonBlock height={14} width={14} />
            </div>
          ))}
        </div>
      </Card>
    );
  }

  if (showError) {
    return (
      <Card
        href="#"
        data-testid="buyItAgain"
        className={`${
          isDesktop && FeatureSwitches.enableNewLoyaltySpendUI
            ? 'sui-w-1/2'
            : ''
        }`}
      >
        <div className="sui-flex sui-flex-row sui-justify-between sui-w-full">
          <Typography weight="bold" variant="h5">
            Buy It Again
          </Typography>
          <Typography> View All </Typography>
        </div>
        <div className="sui-flex sui-gap-3">
          <Typography>
            We were unable to retrieve your Buy It Again details. Refresh the
            page and try again.
          </Typography>
        </div>
      </Card>
    );
  }

  if (buyAgainItems.length > 0) {
    return (
      <Card
        href={`https://${getWindow()?.location.hostname}/buyitagain`}
        className={`${
          isDesktop && FeatureSwitches.enableNewLoyaltySpendUI
            ? 'sui-w-1/2'
            : ''
        }`}
        data-testid="buyItAgain"
        onClick={() => {
          pushEventInfo({
            category: { primaryCategory: 'button submission' },
            eventInfo: { eventName: 'Buy It Again' },
            myAccount: { settings: 'Buy It Again button clicked' },
          });
        }}
      >
        <div className="sui-flex sui-justify-between">
          <Typography style={{ fontWeight: 'bold' }} variant="h5">
            Buy It Again
          </Typography>
          <Typography> View All </Typography>
        </div>

        <div className="sui-flex sui-gap-14 sui-max-h-16">
          {buyAgainItems.map(
            (
              el: { itemDescFromCatalog: string; buyItAgainImage: string },
              i: number,
            ) => {
              if (
                i <
                (isDesktop && FeatureSwitches.enableNewLoyaltySpendUI ? 4 : 8)
              ) {
                return el?.buyItAgainImage ? (
                  <img
                    width="60px"
                    height="60px"
                    src={el.buyItAgainImage}
                    alt={el.itemDescFromCatalog}
                    key={i}
                  />
                ) : (
                  <div key={i}>
                    <img
                      src="https://assets.thdstatic.com/images/v1/no-img.svg"
                      alt="Unavailable"
                      width="60px"
                      height="60px"
                    />
                  </div>
                );
              }
              return null;
            },
          )}
        </div>
      </Card>
    );
  }

  return null;
};
